/* eslint-disable @typescript-eslint/no-explicit-any */
import { Dialog } from 'primereact/dialog';
import { Message } from 'primereact/message';
import { Tag } from 'primereact/tag';
import PageSkeleton from '../../components/common/page-skeleton';
import { Button } from 'sharedUi/Button';
import { useBreadcrumbTitle } from 'sharedUi/BreadcrumbContext';
import { useSecureNavigation } from '../../hooks/useSecureNavigation';
import { useEffect, useState } from 'react';

import { usersService, type User } from '../../services/users.service';
import { personnelLookupService } from '../../services/personnel-lookup.service';
import { projectsService } from '../../services/projects.service';
import { ministriesService } from '../../services/ministries.service';
import EntityLink from '../../components/common/entity-link';
import { extractErrorMessage } from '../../utils/error-handler';
import { useCanUpdate, useCanDelete } from '../../hooks/usePermissions';
import { JOB_NAMES } from '../../constants/jobNames';
import { formatTimestamp } from 'sharedUi/dateUtils';
import { AuditInfoCard } from '../../components/common/audit-info-card';
import UserRolesCard from '../../components/rbac/user-roles-card';
import ScreenHeader from '../../components/common/screen-header';

// Keep in sync with LANGUAGE_OPTIONS in user-management-form.tsx.
const LANGUAGE_NAMES: Record<string, string> = {
  '1': 'English',
  '2': 'Telugu',
  '3': 'Hindi',
  '4': 'Urdu',
};

const boolTag = (value: boolean, trueLabel: string, falseLabel: string) =>
  value ? (
    <Tag value={trueLabel} severity="success" className="text-xs px-1.5 py-0.5" />
  ) : (
    <Tag value={falseLabel} severity="warning" className="text-xs px-1.5 py-0.5" />
  );

const Field = ({ label, value }: { label: string; value: React.ReactNode }) => (
  <div>
    <label className="text-xs font-medium text-ink-muted">{label}</label>
    <p className="text-sm text-ink mt-1">{value ?? '-'}</p>
  </div>
);

const UserManagementView = () => {
  const { id, isReady, navigateToEdit, navigateToList } = useSecureNavigation({
    entity: 'userManagement',
    basePath: '/user-management',
  });

  const canUpdate = useCanUpdate(JOB_NAMES.USER_MANAGEMENT);
  const canDelete = useCanDelete(JOB_NAMES.USER_MANAGEMENT);

  const [data, setData] = useState<User | null>(null);
  const [personnelName, setPersonnelName] = useState<string>('');
  const [appName, setAppName] = useState<string>('');
  const [ministryName, setMinistryName] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  useBreadcrumbTitle(data?.userId);

  useEffect(() => {
    if (!isReady || !id) return;
    (async () => {
      try {
        setLoading(true);
        const res = await usersService.getById(id);
        setData(res);

        if (res.personnelMasterId) {
          const p = await personnelLookupService.getById(res.personnelMasterId);
          setPersonnelName(p?.name || '');
        }
        if (res.appId) {
          try {
            const app = await projectsService.getById(res.appId);
            setAppName(app?.appCode ? `${app.name} (${app.appCode})` : app?.name || '');
          } catch {
            setAppName('');
          }
        }
        if ((res as any).ministryId) {
          try {
            const m = await ministriesService.getById((res as any).ministryId);
            setMinistryName(m?.name || '');
          } catch {
            setMinistryName('');
          }
        }
      } catch (err: any) {
        setError(extractErrorMessage(err, 'Failed to fetch user'));
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isReady, id]);

  const handleDeleteConfirm = async () => {
    if (!id) return;
    try {
      await usersService.delete(id);
      setDeleteDialogOpen(false);
      navigateToList();
    } catch (err: any) {
      setError(extractErrorMessage(err, 'Failed to delete user'));
    }
  };

  if (loading) {
    return (
      <PageSkeleton />
    );
  }

  if (error || !data) {
    return (
      <div className="p-4">
        <Message severity="error" text={error || 'User not found'} className="mb-4 w-full" />
        <Button
          label="Back to User Management"
          icon="pi pi-arrow-left"
          severity="secondary"
          outlined
          onClick={() => navigateToList()}
        />
      </div>
    );
  }

  const deleteDialogFooter = (
    <div className="flex justify-end gap-2">
      <Button label="Cancel" severity="secondary" outlined onClick={() => setDeleteDialogOpen(false)} />
      <Button label="Delete" severity="danger" onClick={handleDeleteConfirm} />
    </div>
  );

  return (
    <div className="p-3" data-testid="SCR-UserManagement-View">
      {/* Header Section */}
      <ScreenHeader icon="pi pi-users" title="User Details" onBack={() => navigateToList()} />

      {/* User Header */}
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-2">
        <div className="flex items-start gap-2.5">
          <div className="w-12 h-12 rounded bg-violet-100 dark:bg-violet-900/30 flex items-center justify-center flex-shrink-0">
            <i className="pi pi-user text-violet-600 dark:text-violet-400" style={{ fontSize: '1.25rem' }} />
          </div>
          <div>
            <div className="flex items-center gap-2 flex-wrap mb-1">
              <h2 className="text-xl font-bold text-ink">
                {data.userId || personnelName || 'User'}
              </h2>
              {data.blocked && <Tag value="Blocked" severity="danger" className="text-xs px-1.5 py-0.5" />}
              {data.isDelete ? (
                <Tag value="Deleted" severity="danger" className="text-xs px-1.5 py-0.5" />
              ) : (
                <Tag value="Active" severity="success" className="text-xs px-1.5 py-0.5" />
              )}
            </div>
            {/* Only users created from a personnel carry personnel details; a
                user created without one shows no personnel line (and never the
                raw ObjectId). */}
            {data.personnelMasterId && (
              <p className="text-xs text-ink-muted">{personnelName || '-'}</p>
            )}
          </div>
        </div>

        {!data.isDelete && (
          <div className="flex gap-1.5 sm:self-center">
            {canUpdate && (
              <Button label="Edit" icon="pi pi-pencil" onClick={() => id && navigateToEdit(id)} size="small" />
            )}
            {canDelete && (
              <Button
                label="Delete"
                icon="pi pi-trash"
                severity="danger"
                onClick={() => setDeleteDialogOpen(true)}
                size="small"
              />
            )}
          </div>
        )}
      </div>

      {/* Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-2">
        {/* Account Information */}
        <div className="ds-card p-3">
          <h2 className="text-sm font-semibold text-ink mb-2">Account Information</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2">
            <Field label="Username" value={data.userId || '-'} />
            {/* Personnel-linked users only — a user created without a personnel
                does not show the Personnel field (or its ObjectId). */}
            {data.personnelMasterId && (
              <Field label="Personnel" value={personnelName || '-'} />
            )}
            <Field label="Project" value={appName || data.appId || '-'} />
            <Field label="Ministry" value={<EntityLink type="ministry" id={(data as any).ministryId} label={ministryName} />} />
            <Field label="MPIN" value={data.mpin ? '••••' : '-'} />
            <Field
              label="Default Language"
              value={
                data.defaultlanguageId !== undefined && data.defaultlanguageId !== null
                  ? LANGUAGE_NAMES[String(data.defaultlanguageId)] || `#${data.defaultlanguageId}`
                  : '-'
              }
            />
          </div>
        </div>

        {/* Status Information */}
        <div className="ds-card p-3">
          <h2 className="text-sm font-semibold text-ink mb-2">Status</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2">
            <Field label="User Status" value={data.userStatus || '-'} />
            <Field label="Blocked" value={boolTag(data.blocked, 'Blocked', 'No')} />
            <Field label="Reason For Block" value={data.reasonForBlock || '-'} />
            <Field label="Registered" value={boolTag(data.isRegistered, 'Yes', 'No')} />
            <Field label="Verified" value={boolTag(data.isVerified, 'Yes', 'No')} />
            <Field label="Logged In" value={boolTag(data.isLoggedIn, 'Yes', 'No')} />
            <Field label="Failed Login Attempts" value={data.failedLoginAttemptCounter ?? 0} />
            <Field
              label="Last Login"
              value={formatTimestamp(data.lastLoginDateTime)}
            />
          </div>
        </div>

        {/* Roles & role bindings in this project */}
        <div className="lg:col-span-2">
          <UserRolesCard userId={data._id} />
        </div>

        {/* Audit Information */}
        <AuditInfoCard
          className="lg:col-span-2"
          createdAt={data.createdAt}
          updatedAt={data.updatedAt}
          createdBy={data.createdBy}
          updatedBy={data.updatedBy}
        />

      </div>

      {/* Delete Confirmation Dialog */}
      <Dialog
        visible={deleteDialogOpen}
        onHide={() => setDeleteDialogOpen(false)}
        header="Delete User"
        footer={deleteDialogFooter}
        style={{ width: '400px' }}
        modal
        dismissableMask
      >
        <div className="flex items-start gap-4">
          <div className="flex-shrink-0 w-10 h-10 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
            <i className="pi pi-exclamation-triangle text-red-600" style={{ fontSize: '1.25rem' }} />
          </div>
          <div>
            <p className="text-sm text-gray-700 dark:text-gray-300">
              Are you sure you want to delete <strong>{data.userId || personnelName}</strong>?
            </p>
            <p className="text-xs text-ink-muted mt-2">This action cannot be undone.</p>
          </div>
        </div>
      </Dialog>
    </div>
  );
};

export default UserManagementView;
